import { takeLatest, call, put } from 'redux-saga/effects';
import { simpleAsyncSaga } from '../helpers/EzeeSaga';
import { EzeeAsyncAction } from '../helpers/EzeeAsyncAction';

import { User, AuthResponse } from '../api/apiTypes';
import { MainReducerState, RequestState } from '../reducers';

import {
    LoginPayload,
    login as loginApiCall,
    logout as logoutApiCall,
    resetPassword as resetPasswordApiCall,
    resetPasswordToken as resetPasswordTokenApiCall,
    checkLoginStatus as checkLoginStatusApiCall,
    ResetPasswordPayload,
} from '../api/auth';
import { combineReducers } from 'redux';
import { DataAction } from '../helpers/EzeeAction';
import {Encryption} from '../../utils/encryption';

export const encryptionConfiguration = {
    secretKey: process.env.REACT_APP_ENCRYPTION_SECRET_KEY,
    secretIv: process.env.REACT_APP_ENCRYPTION_SECRET_IV,
    encryptionMethod: process.env.REACT_APP_ENCRYPTION_METHOD,
};

// State

export interface IsConnectedState<T = any> extends RequestState<T> {
    isConnected: boolean;
}

export interface CheckLoginStatusState extends RequestState {
    user?: User;
    hasCheckedLoginStatus: boolean;
    isConnected: boolean;
}

export interface ResetPasswordStatusState extends RequestState {
    success: boolean;
}

export interface AuthState {
    login: RequestState<User>;
    logout: RequestState;
    resetPassword: ResetPasswordStatusState;
    resetPasswordToken: ResetPasswordStatusState;
    checkLoginStatus: CheckLoginStatusState;
}

const initialState: RequestState = {
    loading: false,
};

const initialResetPasswordState: ResetPasswordStatusState = {
    loading: false,
    success: false,
};

const checkLoginStatusInitialState: CheckLoginStatusState = {
    ...initialState,
    hasCheckedLoginStatus: false,
    isConnected: false,
};

// Actions/Reducers

export const login = new EzeeAsyncAction<AuthState['login'], LoginPayload, AuthResponse>('auth/login', initialState, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
    }),
    success: (state, payload) => ({
        user: payload.user,
        loading: false,
    }),
    failure: (state, payload) => ({
        loading: false,
        error: payload,
    }),
    reset: (state) => initialState,
});

export const resetPassword = new EzeeAsyncAction<
    AuthState['resetPassword'], ResetPasswordPayload
>('auth/resetPassword', initialResetPasswordState, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        success: false,
    }),
    success: (state, payload) => ({
        user: payload.user,
        success: true,
        loading: false,
    }),
    failure: (state, payload) => ({
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => initialResetPasswordState,
});

export const resetPasswordToken = new EzeeAsyncAction<
    AuthState['resetPasswordToken'], ResetPasswordPayload
>('auth/resetPasswordToken', initialResetPasswordState, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        success: false,
    }),
    success: (state, payload) => ({
        success: true,
        loading: false,
    }),
    failure: (state, payload) => ({
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => initialResetPasswordState,
});

export const checkLoginStatus = new EzeeAsyncAction<
    CheckLoginStatusState, any
>('auth/checkLoginStatus', checkLoginStatusInitialState, {
    trigger: (state, payload) => ({
            ...state,
            loading: true,
            hasCheckedLoginStatus: false,
            isConnected: false,
    }),
    success: (state, payload) => ({
        user: new Encryption(encryptionConfiguration).decryptUser(payload),
        hasCheckedLoginStatus: true,
        isConnected: true,
        loading: false,
    }),
    failure: (state, payload) => ({
        hasCheckedLoginStatus: true,
        isConnected: false,
        loading: false,
        error: payload,
    }),
    reset: (state) => checkLoginStatusInitialState,
});

export const logout = new EzeeAsyncAction<AuthState['logout'], any, any, any>('auth/logout', initialState, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
    }),
    success: (state, payload) => ({
        loading: false,
    }),
    failure: (state, payload) => ({
        loading: false,
    }),
    reset: (state) => ({
        ...initialState,
    }),
});

// Reducer

export const authReducer = combineReducers<AuthState>({
    login: login.reducer,
    resetPassword: resetPassword.reducer,
    resetPasswordToken: resetPasswordToken.reducer,
    checkLoginStatus: checkLoginStatus.reducer,
    logout: logout.reducer,
});

// Saga

export function* authSaga() {
    yield takeLatest(login.type.trigger, loginSaga);
    yield takeLatest(resetPassword.type.trigger, resetPasswordSaga);
    yield takeLatest(resetPasswordToken.type.trigger, resetPasswordTokenSaga);
    yield takeLatest(logout.type.trigger, logoutSaga);
    yield takeLatest(checkLoginStatus.type.trigger, simpleAsyncSaga(checkLoginStatusApiCall, checkLoginStatus));
}

function* resetPasswordSaga(actionData: DataAction) {
    try {
        const response = yield call(resetPasswordApiCall, actionData.payload);
        return yield put(resetPassword.success(response));
    } catch (error) {
        return yield put(resetPassword.failure(error));
    }
}

function* resetPasswordTokenSaga(actionData: DataAction) {
    try {
        const response = yield call(resetPasswordTokenApiCall, actionData.payload);
        return yield put(resetPasswordToken.success(response));
    } catch (error) {
        return yield put(resetPasswordToken.failure(error));
    }
}

function* loginSaga(actionData: DataAction) {
    try {
        const response = yield call(loginApiCall, actionData.payload);
        yield put(checkLoginStatus.trigger());
        return yield put(login.success(response));
    } catch (error) {
        return yield put(login.failure(error));
    }
}

function* logoutSaga() {
    try {
        const response = yield call(logoutApiCall);
        yield put(checkLoginStatus.trigger());
        return yield put(logout.success(response));
    } catch (error) {
        return yield put(logout.failure(error));
    }
}

// Store helpers

export const getAuthState = (state: MainReducerState) => state.auth;
export const getLoginState = (state: MainReducerState) => state.auth.login;
export const getUser = (state: MainReducerState) => state.auth.checkLoginStatus.user; // can be undefined
export const getResetPasswordTokenState = (state: MainReducerState) => state.auth.resetPasswordToken;
export const getResetPasswordState = (state: MainReducerState) => state.auth.resetPassword;
export const getCheckLoginStatusState = (state: MainReducerState) => state.auth.checkLoginStatus;
